import React, { useState, useEffect } from 'react';
import { motion } from "framer-motion";
import { Link } from 'react-router-dom';
import { FaLinkedin, FaEnvelope, FaArrowUp } from 'react-icons/fa';
import logo from '../../assets/Logo.png';
import header from '../../assets/header.png'

const TermsPage = () => {
  const [showScrollButton, setShowScrollButton] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.pageYOffset > 300) {
        setShowScrollButton(true);
      } else {
        setShowScrollButton(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8">
      {/* Scroll to top button */}
      {showScrollButton && (
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 z-50 p-3 bg-blue-600 rounded-full shadow-lg hover:bg-blue-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
          aria-label="Scroll to top"
        >
          <FaArrowUp className="text-white text-xl" />
        </motion.button>
      )}

      <div className="max-w-5xl mx-auto">
        {/* Header with navigation-inspired element */}
        <div className="flex justify-between items-center mb-4">
          <div className="w-auto flex-shrink-0">
            <Link to="/">
              <img
                src={header}
                alt="Vorstel logo"
                width="140"
                height="35"
                className="w-[140px] h-auto object-contain cursor-pointer hover:opacity-80 transition-opacity"
              />
            </Link>
          </div>
          <div className={`flex items-center space-x-4`}>
            <a href="mailto:sales@vorstel.com" className="p-1" aria-label="Email Vorstel sales team">
              <FaEnvelope className="text-lg sm:text-xl text-gray-700 hover:text-blue-500" aria-hidden="true" />
            </a>
            <a href="https://www.linkedin.com/company/vorstel-gmbh/about/" target="_blank" rel="noopener noreferrer" className="p-1" aria-label="Vorstel on LinkedIn (opens in a new tab)">
              <FaLinkedin className="text-lg sm:text-xl text-gray-700 hover:text-blue-500" aria-hidden="true" />
            </a>
          </div>
        </div>

        {/* Main content card */}
        <div className="bg-white shadow-xl rounded-lg overflow-hidden">
          {/* Hero section */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 py-12 px-8 text-center">
            <h1 className="text-4xl font-bold text-white mb-3">Terms & Conditions</h1>
            <p className="text-blue-100 max-w-2xl mx-auto">
              Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
          </div>

          {/* Content with sticky sidebar */}
          <div className="flex flex-col lg:flex-row">
            {/* Sticky sidebar navigation */}
            <div className="lg:w-1/4 p-6 bg-gray-50 border-r border-gray-200 lg:sticky lg:top-0 lg:h-screen lg:overflow-y-auto scroll-smooth">
              <h3 className="font-semibold text-gray-700 mb-4">Quick Navigation</h3>
              <ul className="space-y-3">
                {[
                  'Introduction',
                  'Acceptance of Terms',
                  'Intellectual Property',
                  'Purpose-of-the-site',
                  'Third-party-sites',
                  'Governing-law',
                  'Contact'
                ].map((item) => (
                  <li key={item}>
                    <a
                      href={`#${item.toLowerCase().replace(/\s+/g, '-').replace(/[^\w-]+/g, '')}`}
                      className="text-blue-600 hover:text-blue-800 hover:underline text-sm"
                    >
                      {item}
                    </a>
                  </li>
                ))}
              </ul>
            </div>

            {/* Main content */}
            <div className="lg:w-4/4 p-8">
              <div className="prose prose-blue max-w-none">
                {/* Introduction */}
                <section id="introduction" className="mb-12 scroll-mt-20">
                  <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
                    <span className="mr-2">1.</span> Introduction
                  </h2>
                  <div className="space-y-4 text-gray-600">
                    <p>
                      Welcome to Vorstel ("Company", "we", "our", "us"). These Terms of Service ("Terms") govern your use of our website and services ("Services").
                    </p>
                    <p>
                      By accessing or using our Services, you agree to be bound by these Terms. If you disagree with any part of the terms, you may not access the Services.
                    </p>
                    <div className="bg-blue-50 p-4 rounded-md border-l-4 border-blue-500">
                      <p className="font-medium text-blue-800">Important:</p>
                      <p className="text-blue-700">These terms affect your legal rights and obligations. Please read them carefully.</p>
                    </div>
                  </div>
                </section>

                {/* Acceptance of Terms */}
                <section id="acceptance-of-terms" className="mb-12 scroll-mt-20">
                  <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
                    <span className="mr-2">2.</span> Acceptance of Terms
                  </h2>
                  <div className="space-y-4 text-gray-600">
                    <p>
                      These Terms of Use ("Terms") govern your access to and use of the Vorstel website (the "Site"). By accessing, browsing, or using the Site, or by downloading any content from it, you agree to be legally bound by these Terms, which form a contract between you and Vorstel ("Vorstel," "we," "us," or "our").
                    </p>

                    <h3 className="text-lg font-semibold text-gray-700 mt-6">Individual Users:</h3>
                    <p>
                      If you are an individual user, you represent and warrant that you are of legal age (the age of majority) in the jurisdiction where you reside.
                    </p>

                    <h3 className="text-lg font-semibold text-gray-700 mt-6">Organizational Users:</h3>
                    <p>
                      If you access or use the Site on behalf of, or for the benefit of, any company, organization, or other entity (an "Organization"), you are agreeing to these Terms on behalf of both yourself and that Organization. You represent and warrant that you have the necessary legal authority to bind the Organization to these Terms. In such cases, references to "you" or "your" throughout these Terms apply to both the individual user and the represented Organization.
                    </p>

                    <div className="bg-blue-50 p-4 rounded-lg mt-6">
                      <h3 className="font-semibold text-blue-800 mb-3">Key Improvements to Our Terms:</h3>
                      <ul className="list-disc pl-5 space-y-2 text-blue-700">
                        <li>
                          <span className="font-medium">Stronger Opening:</span> Clearly states the purpose ("govern your access to and use") and the binding nature ("legally bound," "contract between you and Vorstel").
                        </li>
                        <li>
                          <span className="font-medium">Conciseness:</span> Removed redundant phrases like "state the terms and conditions governing" and "constitute a legally binding contract.
                        </li>
                        <li>
                          <span className="font-medium">Action-Oriented:</span> Uses active verbs ("By accessing, browsing, or using") instead of passive phrasing.
                        </li>
                        <li>
                          <span className="font-medium">Clear Acceptance:</span> Simplified to the direct "agree to be legally bound by these Terms.
                        </li>
                        <li>
                          <span className="font-medium">Defined Parties:</span> Clearly names "Vorstel" as the counterparty.
                        </li>
                      </ul>
                    </div>

                  </div>

                  <div className="bg-yellow-50 p-4 rounded-md border-l-4 border-yellow-400 mt-6">
                    <p className="font-medium text-yellow-800">Note:</p>
                    <p className="text-yellow-700">Your continued use of the Site constitutes ongoing acceptance of these Terms and any updates to them.</p>
                  </div>
                </section>

                {/* Intellectual Property */}
                <section id="intellectual-property" className="mb-12 scroll-mt-20">
                  <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
                    <span className="mr-2">3.</span> Intellectual Property
                  </h2>
                  <div className="space-y-6 text-gray-600">
                    {/* Ownership */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">Ownership by Vorstel:</h3>
                      <p>
                        The Vorstel website (the "Site"), including all content, features, functionality, designs, text, graphics, logos, icons, images, audio/video clips, software, data compilations, and the underlying source code (collectively, the "Content"), is the exclusive property of Vorstel or our licensors. This Content is protected by copyright, trademark, patent, trade secret, and other intellectual property laws worldwide.
                      </p>
                    </div>

                    {/* License */}
                    <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500">
                      <h3 className="text-lg font-semibold text-blue-800 mb-2">Limited License to Users:</h3>
                      <p className="mb-3">Vorstel grants you a limited, non-exclusive, non-transferable, revocable license to:</p>
                      <ul className="list-disc pl-5 space-y-1">
                        <li>Access and view the Site and Content for your personal, non-commercial informational use only</li>
                        <li>Download or print discrete portions of publicly accessible Content solely for personal reference</li>
                      </ul>
                      <p className="mt-3 font-medium">This license does not grant you any ownership rights.</p>
                    </div>

                    {/* Prohibited Uses */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">Prohibited Uses:</h3>
                      <p className="mb-3">You agree NOT to:</p>
                      <ul className="list-disc pl-5 space-y-2">
                        <li>Copy, reproduce, modify, adapt, translate, or create derivative works of any Content</li>
                        <li>Distribute, sell, license, lease, publicly perform, display, transmit, or commercially exploit any Content</li>
                        <li>Reverse engineer, decompile, disassemble, or attempt to derive the source code of any Site software</li>
                        <li>Use data mining, robots, scraping, or similar data gathering tools on the Site</li>
                        <li>Remove, alter, or obscure any copyright, trademark, or other proprietary notices</li>
                        <li>"Frame" or "mirror" any part of the Site without our prior written consent</li>
                        <li>Use the Content for any public or commercial purpose without express written permission</li>
                      </ul>
                    </div>

                    {/* Trademarks */}
                    <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-400">
                      <h3 className="text-lg font-semibold text-yellow-800 mb-2">Trademarks:</h3>
                      <p>
                        The Vorstel name, logo, and all related product and service names, or designs ("Trademarks") are trademarks of Vorstel or its affiliates/licensors. You may not use any Trademarks without our prior written permission. All other trademarks are property of their respective owners.
                      </p>
                    </div>

                    {/* User-Generated Content */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">User-Generated Content:</h3>
                      <p>
                        If the Site permits you to post content, you retain ownership of your original content. However, by submitting it, you grant Vorstel a worldwide, royalty-free, perpetual, irrevocable, sublicensable, non-exclusive license to use, copy, modify, adapt, publish, translate, distribute, and display that content in connection with our business. You represent you have all rights necessary to grant this license.
                      </p>
                    </div>

                    {/* Feedback */}
                    <div className="bg-purple-50 p-4 rounded-lg border-l-4 border-purple-400">
                      <h3 className="text-lg font-semibold text-purple-800 mb-2">Feedback:</h3>
                      <p>
                        Any feedback, suggestions, or ideas you voluntarily provide to Vorstel regarding the Site or our services becomes our sole property. We may use this feedback for any purpose without compensation or obligation to you.
                      </p>
                    </div>

                    {/* Respect IP */}
                    <div className="mt-4">
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">Respect Our IP:</h3>
                      <p>
                        Unauthorized use of the Site or Content may violate intellectual property laws and could result in legal action. Vorstel reserves all rights not expressly granted in these Terms.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Purpose of the Site */}
                <section id="purpose-of-the-site" className="mb-12 scroll-mt-20">
                  <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
                    <span className="mr-2">4.</span> Purpose of the Site
                  </h2>
                  <div className="space-y-6 text-gray-600">
                    {/* Informational Resource */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">Informational Resource:</h3>
                      <p>
                        The Vorstel website (the "Site") is provided solely as an informational resource about our company, software solutions, services, and related industry insights. All content (including text, graphics, data, and descriptions) is for general informational purposes only.
                      </p>
                    </div>

                    {/* Not Professional Advice */}
                    <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-400">
                      <h3 className="text-lg font-semibold text-red-800 mb-2">Not Professional Advice:</h3>
                      <p>
                        Nothing on this Site constitutes professional advice (including technical, legal, financial, or business advice). You should not rely on Site content as a substitute for independent professional judgment or consultation.
                      </p>
                    </div>

                    {/* No Service Commitments */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">No Service Commitments:</h3>
                      <p>
                        The availability of information about Vorstel's software or services on the Site does not constitute an offer to provide those services or software to you. Formal agreements are only formed through separate, mutually executed contracts.
                      </p>
                    </div>

                    {/* Permitted Use */}
                    <div className="bg-green-50 p-4 rounded-lg border-l-4 border-green-400">
                      <h3 className="text-lg font-semibold text-green-800 mb-2">Permitted Use:</h3>
                      <p>
                        You agree to access and use the Site only for its intended purpose: to learn about Vorstel and our offerings in a lawful manner.
                      </p>
                    </div>

                    {/* Prohibited Uses */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">Prohibited Uses:</h3>
                      <p className="mb-3">You agree NOT to use the Site for any purpose that is:</p>
                      <ul className="list-disc pl-5 space-y-2">
                        <li>Unlawful, fraudulent, or harmful</li>
                        <li>Intended to exploit, harm, or attempt to exploit or harm minors</li>
                        <li>Intended to gain unauthorized access to systems, data, or networks</li>
                        <li>Disruptive (e.g., spamming, transmitting malware, overloading infrastructure)</li>
                        <li>Competitive intelligence gathering beyond fair use</li>
                      </ul>
                    </div>

                    {/* Accuracy and Changes */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">Accuracy and Changes:</h3>
                      <p>
                        While we strive for accuracy, Vorstel does not warrant that all information on the Site is current, complete, or error-free. We reserve the right to modify, suspend, or discontinue any aspect of the Site at any time without notice.
                      </p>
                    </div>

                    {/* No Endorsement */}
                    <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
                      <h3 className="text-lg font-semibold text-blue-800 mb-2">No Endorsement:</h3>
                      <p>
                        References to third-party products, services, websites, or organizations on the Site are for informational purposes only and do not constitute an endorsement by Vorstel
                      </p>
                    </div>

                    {/* Important Note */}
                    <div className="bg-yellow-50 p-4 rounded-lg border-l-4 border-yellow-400">
                      <h3 className="text-lg font-semibold text-yellow-800 mb-2">Important Note:</h3>
                      <p className="font-medium">
                        THE SITE AND ITS CONTENT ARE PROVIDED "AS IS" AND "AS AVAILABLE." Vorstel DISCLAIMS ALL WARRANTIES REGARDING THE SITE'S SUITABILITY FOR ANY PARTICULAR PURPOSE OR USE.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Links to Third-Party Sites */}
                <section id="third-party-sites" className="mb-12 scroll-mt-20">
                  <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
                    <span className="mr-2">5.</span> Links to Third-Party Sites
                  </h2>
                  <div className="space-y-6 text-gray-600">
                    {/* Introductory paragraph */}
                    <p>
                      Our Site may contain links to independent third-party websites ("Third-Party Sites") for convenience only.
                    </p>

                    {/* No Endorsement */}
                    <div className="bg-orange-50 p-4 rounded-lg border-l-4 border-orange-400">
                      <h3 className="text-lg font-semibold text-orange-800 mb-2">No Endorsement:</h3>
                      <p>
                        Vorstel does not endorse, control, or assume responsibility for Third-Party Sites or their content.
                      </p>
                    </div>

                    {/* Use at Your Risk */}
                    <div>
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">Use at Your Risk:</h3>
                      <p>
                        You access Third-Party Sites entirely at your own risk.
                      </p>
                    </div>

                    {/* Third-Party Terms Govern */}
                    <div className="bg-blue-50 p-4 rounded-lg border-l-4 border-blue-400">
                      <h3 className="text-lg font-semibold text-blue-800 mb-2">Third-Party Terms Govern:</h3>
                      <p>
                        Your interactions with Third-Party Sites are governed by their terms and policies, not ours.
                      </p>
                    </div>

                    {/* Liability Disclaimer */}
                    <div className="bg-red-50 p-4 rounded-lg border-l-4 border-red-400">
                      <h3 className="text-lg font-semibold text-red-800 mb-2">Liability Disclaimer:</h3>
                      <p className="font-medium">
                        Vorstel DISCLAIMS ALL LIABILITY ARISING FROM YOUR USE OF THIRD-PARTY SITES.
                      </p>
                    </div>
                  </div>
                </section>

         {/* Governing Law */}
<section id="governing-law" className="mb-12 scroll-mt-20">
  <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
    <span className="mr-2">6.</span> Governing Law
  </h2>
  <div className="space-y-4 text-gray-600">
    <p>
      These Terms and your use of the Site are governed by the laws of the <b>Federal Republic of Germany</b>, without regard to its conflict of law principles. Any disputes will be resolved exclusively in the competent courts located in <b>Mannheim, Germany</b>.
    </p>
    <div className="bg-purple-50 p-6 rounded-md border-l-4 border-purple-400">
      <p className="text-purple-800 p-2">
        Clear Jurisdiction: Explicitly names German law (appropriate for entities based in Germany).
      </p>
      <p className="text-purple-800 p-2">
        Conflict of Laws Waiver: Standard "without regard to conflict of laws" language.
      </p>
      <p className="text-purple-800 p-2">
        Exclusive Venue: Specifies Mannheim courts for dispute resolution.
      </p>
      <p className="text-purple-800 p-2">
        Binding Scope: Covers both the Terms and Site use.
      </p>
    </div>
  </div>
</section>


                {/* Contact */}
                <section id="contact" className="mb-12 scroll-mt-20">
                  <h2 className="text-2xl font-semibold text-gray-800 mb-4 flex items-center">
                    <span className="mr-2">7.</span> Contact
                  </h2>
                  <div className="space-y-4 text-gray-600">
                    <p>
                      For any questions about these Terms, please contact us at:
                    </p>
                    <div className="bg-blue-50 p-4 rounded-md">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <h4 className="font-medium text-blue-800 mb-2">Mailing Address:</h4>
                          <p>Richard-Wagner-Straße 4,</p>
                          <p>68165, Mannheim</p>
                          <p>Deutschland</p>
                        </div>
                        <div>
                          <h4 className="font-medium text-blue-800 mb-2">Contact Information:</h4>
                          <p>Email:contact@vorstel.com</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </section>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="mt-12 text-center text-gray-500 text-sm">
          <p>&copy; {new Date().getFullYear()} Vorstel All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;